if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        if (document.querySelector('.sticky-cta') || this.closest('.amc-card-purchase-row, .amc-card-option-modal')) {
         this.init();
        } else {
          window.initScriptOnDemand(this, this.init.bind(this), 100);
        }
      }

      connectedCallback() {
        // The constructor performs the first initialization. A detached and
        // reinserted form needs the listener rebound after disconnectedCallback
        // has cleaned it up, while the first connection should preserve the
        // existing lazy-init behavior.
        if (this.__amcHasConnected && !this.__amcInitialized) {
          if (document.querySelector('.sticky-cta') || this.closest('.amc-card-purchase-row, .amc-card-option-modal')) {
            this.init();
          } else if (typeof window.initScriptOnDemand === 'function') {
            window.initScriptOnDemand(this, this.init.bind(this), 100);
          } else {
            this.init();
          }
        }
        this.__amcHasConnected = true;
      }

      init() {
        if (this.__amcInitialized) return;

        this.form = this.querySelector('form');
        const variantIdInput = this.form?.querySelector('[name="id"]');
        const submitButton = this.querySelector('button[type="submit"][name="add"], .product-form__submit');

        // Product forms can be upgraded before Liquid or a quick-add modal has
        // finished inserting all controls. Wait for the complete native
        // contract instead of marking the element initialized and throwing on a
        // missing hidden variant input or submit button.
        if (!this.form || !variantIdInput || !submitButton) {
          this.observeFormLifecycle();
          return;
        }

        if (this.__amcFormObserver) {
          this.__amcFormObserver.disconnect();
          this.__amcFormObserver = null;
        }

        this.submitButton = submitButton;
        this.__amcInitialized = true;
        variantIdInput.disabled = false;
        this.__amcVariantIdInput = variantIdInput;
        this.submitHandler = this.submitHandler || this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.submitHandler);
        this.cartDrawer = document.querySelector('cart-drawer');
        this.cartItems = document.querySelector('cart-items');
        this.cartNotification = document.querySelector('cart-notification');
        this.ctaButton = !this.closest('.section-featured-product') ?
          this.closest('.section')?.querySelector('.sticky-cta .button') : null;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.ctaButton) {
          this.ctaButtonListener = this.ctaButtonListener || this.ctaButtonClickHandler.bind(this);
          this.ctaButton.addEventListener('click', this.ctaButtonListener);
        }

        // A homepage shelf can contain dozens of product forms. Request each shared
        // cart surface once instead of re-running its section loader for every card.
        if (this.cartDrawer && !this.cartDrawer.__amcInitRequested) {
          this.cartDrawer.__amcInitRequested = true;
          customElements.whenDefined('cart-drawer').then(() => {
            this.cartDrawer.init();
          });
        }

        if (this.cartNotification && !this.cartNotification.__amcInitRequested) {
          this.cartNotification.__amcInitRequested = true;
          customElements.whenDefined('cart-notification').then(() => {
            this.cartNotification.init();
          });
        }

        this.observeFormLifecycle();
      }

      observeFormLifecycle() {
        if (this.__amcFormObserver || typeof MutationObserver === 'undefined') return;

        this.__amcFormObserver = new MutationObserver(() => {
          const currentForm = this.querySelector('form');
          if (!currentForm) {
            if (this.__amcInitialized) this.resetFormBinding();
            return;
          }

          const currentVariantIdInput = currentForm.querySelector('[name="id"]');
          const currentSubmitButton = this.querySelector('button[type="submit"][name="add"], .product-form__submit');
          if (
            this.__amcInitialized &&
            currentForm === this.form &&
            currentVariantIdInput === this.__amcVariantIdInput &&
            currentSubmitButton === this.submitButton
          ) return;

          if (this.__amcInitialized) {
            this.resetFormBinding();
          }

          this.form = currentForm;
          if (!currentForm || !currentVariantIdInput || !currentSubmitButton) return;

          this.__amcFormObserver.disconnect();
          this.__amcFormObserver = null;
          this.init();
        });
        this.__amcFormObserver.observe(this, { childList: true, subtree: true });
      }

      resetFormBinding() {
        if (this.form && this.submitHandler) {
          this.form.removeEventListener('submit', this.submitHandler);
        }
        this.submitHandler = null;
        this.__amcInitialized = false;
        this.submitButton = null;
        this.__amcVariantIdInput = null;
      }

      disconnectedCallback() {
        if (this.__amcFormObserver) {
          this.__amcFormObserver.disconnect();
          this.__amcFormObserver = null;
        }
        this.resetFormBinding();
        if (this.ctaButton) {
          this.ctaButton.removeEventListener('click', this.ctaButtonListener);
        }
        this.ctaButton = null;
      }

      ctaButtonClickHandler() {
        if (this.submitButton && this.submitButton.classList.contains('product-form__submit')) {
          this.submitButton.click();
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const embroideryText = this.form.querySelector('[data-embroidery-text]:not(:disabled)');
        if (embroideryText && !embroideryText.reportValidity()) return;
        if (this.form.dataset.amcWalkinForm === 'true') {
          evt.stopImmediatePropagation();
          const configuredPurchase = this.closest('product-info')?.amcAddConfiguredBuild;
          if (typeof configuredPurchase === 'function') {
            configuredPurchase(evt);
          } else {
            this.handleErrorMessage('The complete-build configurator is still loading. Please wait, then select your required options. Nothing has been added.');
          }
          return;
        }
        const submitButton = this.submitButton;
        const ctaButton = this.ctaButton;
        if (!submitButton) return;

        // Variant changes can replace a form while its request is still in flight.
        // Keep the request state outside the replaceable element as well as the button.
        const requestKey = this.form.getAttribute('id') || this.closest('product-info')?.getAttribute('id') || this.variantIdInput?.value;
        const requests = window.__amcNativeCartRequests || (window.__amcNativeCartRequests = new Map());
        const previousRequest = requests.get(requestKey);
        const viewCart = () => window.location.assign(window.routes?.cart_url || '/cart');
        if (previousRequest?.uncertain) {
          viewCart();
          return;
        }
        if (previousRequest?.pending || submitButton.getAttribute('aria-disabled') === 'true') return;
        const requestState = { pending: true, uncertain: false };
        requests.set(requestKey, requestState);

        let requestStarted = false;
        let confirmedAdded = false;
        let navigatingToCart = false;
        this.error = false;
        this.handleErrorMessage();
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden', 'spinning-complete');
        if (ctaButton) {
          ctaButton.setAttribute('aria-disabled', 'true');
          ctaButton.querySelector('.loading__spinner')?.classList.remove('hidden', 'spinning-complete');
          ctaButton.classList.add('loading');
        }

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          const formData = new FormData(this.form);
          if (this.form.querySelector('[data-amc-cot-accessories]') && !window.amcAppendAccessoryItems) {
            throw new Error('The accessory selector is still loading. Please reload before adding your selected items.');
          }
          window.amcAppendAccessoryItems?.(this.form, formData);
          window.amcRackAddonsAppend?.(this.form, formData);
          const surfaces = [];
          const sections = [];
          const action = window.theme?.settings?.cartDrawer?.atcAction;
          const requestedSurfaces = action === 'drawer'
            ? [[this.cartDrawer, 'id']]
            : action === 'notification'
              ? [[this.cartNotification, 'id']]
              : [[this.cartItems, 'section']];

          // The cart UI is optional. An unready drawer must never prevent Shopify
          // from receiving a valid product request.
          for (const [surface, key] of requestedSurfaces) {
            if (!surface || typeof surface.getSectionsToRender !== 'function') continue;
            try {
              if (surface === this.cartNotification) window.loadTemplateContent?.(surface);
              const surfaceSections = surface.getSectionsToRender();
              sections.push(...surfaceSections.map(section => section[key]).filter(Boolean));
              if (typeof surface.renderContents === 'function') surfaces.push(surface);
            } catch (error) {
              console.warn('[product-form] Cart display unavailable; using the cart page.', error);
            }
          }
          if (sections.length) {
            formData.append('sections', [...new Set(sections)]);
            formData.append('sections_url', window.location.pathname);
          }
          const activeSurface = action === 'drawer' ? this.cartDrawer : this.cartNotification;
          try {
            activeSurface?.setActiveElement?.(document.activeElement);
          } catch (error) {
            console.warn('[product-form] Cart focus could not be prepared.', error);
          }

          config.body = formData;
          requestStarted = true;
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 20000);
          let httpResponse, response;
          try {
            httpResponse = await fetch(routes.cart_add_url, { ...config, signal: controller.signal });
            response = await httpResponse.json();
          } finally {
            clearTimeout(timeout);
          }
          if (Number(response.status || httpResponse.status) >= 500) throw new Error('Cart service response could not confirm the update');
          if (response.status || !httpResponse.ok) {
            // An explicit Shopify rejection is safe to correct and resubmit.
            requestStarted = false;
            this.handleErrorMessage(response.description || response.message || 'This item could not be added. Please check your selection.');
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });
            return;
          }
          if (!response.id && !Array.isArray(response.items)) throw new Error('Unrecognized cart response');
          confirmedAdded = true;
          this.handleErrorMessage();

          if (action === 'page' || !surfaces.length || !response.sections ||
              (action === 'drawer' && !surfaces.includes(this.cartDrawer)) ||
              (action === 'notification' && !surfaces.includes(this.cartNotification))) {
            navigatingToCart = true;
            viewCart();
            return;
          }
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });

          const renderCart = () => surfaces.forEach(surface => {
            surface.classList.remove('is-empty');
            surface.renderContents(response);
          });
          const optionModal = this.closest('.amc-card-option-modal');
          if (optionModal && typeof optionModal.hide === 'function') optionModal.hide(true);
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal && !window.theme?.settings?.cartDrawer?.muteNotification) {
            await new Promise((resolve, reject) => {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => {
                  try { renderCart(); resolve(); } catch (error) { reject(error); }
                });
              }, { once: true });
              quickAddModal.hide(true);
            });
          } else {
            renderCart();
          }
          ctaButton?.classList.add('added-to-cart');
          submitButton.classList.add('added-to-cart');
        } catch (error) {
          console.error('[product-form] Cart request or display failed.', error);
          if (confirmedAdded) {
            // Shopify already added it. Rendering failure must not invite a
            // second purchase request and duplicate the selected quantity.
            navigatingToCart = true;
            viewCart();
          } else if (requestStarted) {
            requestState.uncertain = true;
            this.handleErrorMessage('We could not confirm the cart update. Review your cart before adding this item again.');
            const wrapper = this.querySelector('.product-form__error-message-wrapper');
            if (wrapper && !this.hideErrors) {
              const link = document.createElement('a');
              link.href = window.routes?.cart_url || '/cart';
              link.textContent = 'View cart';
              link.className = 'product-form__cart-recovery';
              wrapper.appendChild(link);
            } else {
              navigatingToCart = true;
              viewCart();
            }
          } else {
            this.handleErrorMessage('This item could not be added. Please try again.');
          }
        } finally {
          requestState.pending = navigatingToCart;
          if (!requestState.uncertain && !navigatingToCart && requests.get(requestKey) === requestState) requests.delete(requestKey);
          submitButton.classList.remove('loading');
          if (!navigatingToCart) submitButton.removeAttribute('aria-disabled');
          setTimeout(() => {
            ctaButton?.classList.remove('added-to-cart');
            submitButton.classList.remove('added-to-cart');
          }, 1000);
          if (ctaButton) {
            ctaButton.classList.remove('loading');
            if (!navigatingToCart) ctaButton.removeAttribute('aria-disabled');
            ctaButton.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');
          }
          this.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');
          publish('quick-buy-action-complete');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) {
          if (!errorMessage) return;
          // Simple collection cards and homepage shelves omit the PDP error UI.
          // Give their Shopify rejection an accessible, visible place to appear.
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.prepend(this.errorMessageWrapper);
        }
        this.errorMessageWrapper.querySelectorAll('.product-form__cart-recovery').forEach(link => link.remove());
        this.errorMessage = this.errorMessageWrapper.querySelector('.product-form__error-message');
        if (!this.errorMessage) {
          this.errorMessage = document.createElement('span');
          this.errorMessage.className = 'product-form__error-message';
          this.errorMessageWrapper.appendChild(this.errorMessage);
        }

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;

          // Scroll to the error if needed
          const elementRect = this.errorMessage.getBoundingClientRect();
          const isVisible = elementRect.top >= 0 && elementRect.bottom <= window.innerHeight && elementRect.top >= 100;

          // If the element is not visible or if the top of the element is closer than 100px to the top of the viewport
          if (!isVisible) {
            const topPosition = elementRect.top + window.pageYOffset - 150; // Calculate position with 150px padding

            window.scrollTo({
              top: topPosition,
              behavior: 'smooth'
            });
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        let price;
        if (this.form.dataset.showPrice === 'true') {
          const productInfo = this.form.closest('.product__info-wrapper');
          const priceElem = productInfo?.querySelector('.price__container .price-item--last');
          const priceText = priceElem?.textContent.trim();
          if (priceText && priceText.length > 0) price = priceText;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.ctaButton?.setAttribute('disabled', 'disabled');

          if (text) {
            this.submitButton.querySelector('span').textContent = `${text}${price ? ' - ' + price : ''}`;
            if(this.ctaButton) this.ctaButton.querySelector('span').textContent = text;
          }

          if (text === window.variantStrings.soldOut) this.closest('.product')?.setAttribute('data-variant-status', 'out-of-stock');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.ctaButton?.removeAttribute('disabled');
          const buttonText = this.submitButton.dataset.isPreorder ? window.variantStrings.preorder : window.variantStrings.addToCart;
          this.submitButton.querySelector('span').textContent = `${buttonText}${price ? ' - ' + price : ''}`;
          if(this.ctaButton) this.ctaButton.querySelector('span').textContent = buttonText;
          this.closest('.product')?.setAttribute('data-variant-status', 'in-stock');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
